import React, {useEffect, useRef} from 'react';

function truncateDefinition(definition) {
  const text = String(definition || '').trim();
  if (text.length <= 110) return text;
  return `${text.slice(0, 107).trim()} (...)`;
}

const FrontloadingPhase = ({
  data,
  activeIndex = 0,
  finished = false,
  onStart,
  onRestart,
  showDisclaimer = false,
  onDismissDisclaimer,
  listeningEnabled = true,
}) => {
  if (!data) return null;

  const {terms} = data;
  const activeCardRef = useRef(null);
  const columnCount = terms.length < 5 ? 2 : 3;

  useEffect(() => {
    if (!listeningEnabled) return;
    activeCardRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
    });
  }, [activeIndex, listeningEnabled]);

  return (
    <div className="lcv-frontload-panel">
      {showDisclaimer && (
        <div className="lcv-fl-disclaimer">
          <div className="lcv-fl-disclaimer-icon">i</div>
          <div className="lcv-fl-disclaimer-body">
            <strong>Hint Placeholder</strong>
            <span>
              This banner is standing in as a product hint area while the final
              intro guidance is still being defined. Please focus on the
              frontloading <em>UI/UX</em> for now.
            </span>
          </div>
          <button
            type="button"
            className="lcv-fl-disclaimer-close"
            onClick={onDismissDisclaimer}
          >
            ✕
          </button>
        </div>
      )}

      <div className="lcv-frontload-header">
        <span className="lcv-fl-badge">INTRO</span>
        <h2 className="lcv-fl-title">Key Terms for This Lesson</h2>
        <p className="lcv-fl-subtitle">
          {terms.length} vocabulary terms to master
        </p>
      </div>

      <div
        className={`lcv-fl-grid lcv-fl-grid-cols-${columnCount}`}
        style={{gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))`}}
      >
        {terms.map((term, index) => {
          const isActive = listeningEnabled && index === activeIndex && !finished;
          const isDone = listeningEnabled
            ? index < activeIndex ||
              (finished && index === terms.length - 1) ||
              (finished && index < terms.length)
            : false;

          return (
            <div
              key={term.term || index}
              ref={index === activeIndex ? activeCardRef : undefined}
              className={[
                'lcv-fl-card',
                isActive ? 'lcv-fl-active' : '',
                isDone && !isActive ? 'lcv-fl-done' : '',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              <span className="lcv-fl-num">
                {isDone && !isActive ? '✓' : index + 1}
              </span>

              <div className="lcv-fl-content">
                <div className="lcv-fl-term">{term.term}</div>
                <div className="lcv-fl-def">{truncateDefinition(term.definition)}</div>
              </div>

              {listeningEnabled && isActive && (
                <div className="lcv-fl-active-indicator">
                  <span className="lcv-fl-active-pulse" />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {finished ? (
        <div className="lcv-fl-buttons">
          {listeningEnabled && (
            <button
              type="button"
              className="lcv-btn-secondary lcv-btn-watch-again"
              onClick={onRestart}
            >
              ↻ Watch Again
            </button>
          )}
          <button type="button" className="lcv-btn-primary" onClick={onStart}>
            Start Learning →
          </button>
        </div>
      ) : (
        <div className="lcv-fl-progress-hint">
          <span className="lcv-fl-listening-dot" />
          Listening... {Math.min(activeIndex + 1, terms.length)} of {terms.length}
        </div>
      )}
    </div>
  );
};

export default FrontloadingPhase;
